import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useFieldArray, useForm } from "react-hook-form";
import { getUser } from "../../../../pages/userSlice";

import { CreateEmployees } from "../../../../services/user/infonavit/infonavitRequests";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { CODES } from "../../../../consts/codes";
import ModalInfo from "../../Modals/modalInfo";
import { REGEXP } from "../../../../consts/regexp";

import { EmployeesForm } from "./employeesForm";

const AddEmployees = ({ nrpList, setCreatedEmployees }) => {
  /**
   * Use Selector
   */

  const userInfo = useSelector(getUser);

  /**
   * Title Container component
   */
  const DialogTitleContainer = (props) => {
    const { children, onClose, ...other } = props;

    return (
      <DialogTitle {...other}>
        {children}
        {onClose ? (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </DialogTitle>
    );
  };

  /**
   * useTranslation
   */
  const { t } = useTranslation();

  /**
   * Schema
   */
  const schema = yup.object().shape({
    employees: yup.array().of(
      yup.object().shape({
        nrp: yup
          .string()
          .required(t("App.validationMessages.required"))
          .matches(
            REGEXP.INFONAVIT.NRP,
            t("App.validationMessages.infonavit.nrp")
          ),
        nss: yup
          .string()
          .required(t("App.validationMessages.required"))
          .matches(
            REGEXP.INFONAVIT.NSS,
            t("App.validationMessages.infonavit.nss")
          ),
      })
    ),
  });

  /**
   * useForm
   */
  const {
    handleSubmit,
    setValue,
    reset,
    getValues,
    watch,
    formState: { errors },
    control,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      employees: [{ nrp: "", nss: "" }],
    },
  });

  /**
   * useFieldArray que maneja el arreglo de empleados
   */
  const {
    fields: employeesFields,
    append: permissionsAppend,
    remove: permissionsRemove,
    update: permissionsUpdate,
  } = useFieldArray({
    control,
    name: "employees",
  });

  /**
   * Use State
   */

  const [openCreateEmployees, setOpenCreateEmployees] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [isDesktop, setDesktop] = useState(window.innerWidth > 576);

  const [openModalInfo, setOpenModalInfo] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [createEmployeesData, setCreateEmployeesData] = useState({});

  /**
   * Use Effect
   */

  /**
   * useEffect para actualizar el tamaño de pantalla actual de la pantalla
   */
  useEffect(() => {
    window.addEventListener("resize", updateMedia);
    return () => window.removeEventListener("resize", updateMedia);
  });

  /**
   * Handles
   */

  /**
   * Función para añadir un empleado
   */
  const handleAddNewEmployee = () => {
    permissionsAppend("");
  };

  /**
   *
   * Función para remover un empleado del arreglo de empleados
   *
   * @param {String} id
   * @param {Number} index
   */
  const handleRemoveEmployee = (index) => {
    permissionsRemove(index);
  };

  /**
   * Función que abre el modal
   */
  const handleOpen = () => {
    reset();
    setOpenCreateEmployees(true);
  };

  /**
   * Función para cerrar el modal
   */
  const handleClose = async () => {
    setOpenCreateEmployees(false);
    setLoadingCreate(false);
    reset();
  };

  /**
   * Método para actualizar el tamaño de pantalla actual
   */
  const updateMedia = () => {
    setDesktop(window.innerWidth > 490);
  };

  /**
   *
   * Función que construye el objeto de usuario
   *
   * @param {Object} data información del usuario
   * @returns objeto de usuario según formato
   */
  const handleCreateCredentialsPayload = (data) => {
    const payload = {
      mainUser: userInfo.mainUser,
      ...data,
    };

    return payload;
  };

  /**
   * Función que retorna el título del modal según código de respuesta
   * @param {Number} responseCode código de respuesta
   * @returns título según código
   */
  const handleModalTitle = (responseCode) => {
    if (responseCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST) {
      return "";
    } else if (responseCode === 1001) {
      return t("App.userDetail.modalTitle.title2");
    } else if (responseCode === 1002) {
      return t("App.userDetail.modalTitle.title1");
    } else {
      return "";
    }
  };

  /**
   *
   * Function to create a new employees
   *
   * @param {Object} data
   */
  const handleCreateEmployees = async (data) => {
    try {
      setLoadingCreate(true);

      const employeesPayload = handleCreateCredentialsPayload(data);

      const {
        status,
        data: { responseStatus, response },
      } = await CreateEmployees(employeesPayload);

      if (
        status === CODES.COD_RESPONSE_HTTP_OK &&
        responseStatus.statusCode === CODES.S_COD_RESPONSE_SUCCESS_REQUEST
      ) {
        setCreatedEmployees([...data.employees]);
      }

      setModalTitle(handleModalTitle(responseStatus.statusCode));
      setOpenModalInfo(true);
      setCreateEmployeesData({ data: { responseStatus, response }, status });

      setLoadingCreate(false);
    } catch (error) {
      console.log("====================================");
      console.log(error);
      console.log("====================================");
    }
  };

  return (
    <>
      <Button
        type="button"
        startIcon={
          <AddIcon className="no-search-input" sx={{ fontSize: 22 }} />
        }
        className="btn__filled__blue btn__filled__blue--real"
        onClick={handleOpen}
      >
        {t("App.infonavitRequests.btnGroup.addEmployees")}
      </Button>

      <Dialog
        onClose={handleClose}
        open={openCreateEmployees}
        sx={{
          "& .MuiDialog-container": {
            "& .MuiPaper-root": {
              width: "100%",
              height: "100%",
              maxWidth: "900px",
              maxHeight: "520px",
              boxShadow: "none",
              borderRadius: "22px",
              padding: "2rem",
            },
          },
        }}
      >
        <DialogTitleContainer onClose={handleClose}>
          <Grid container direction="column" rowSpacing={1}>
            <Grid item className="display-large__primary-one">
              {t("App.infonavitRequests.addEmployees.title")}
            </Grid>
          </Grid>
        </DialogTitleContainer>

        <DialogContent
          className="scroll-table"
          style={{ padding: "20px 10px" }}
        >
          <form
            id="form-employees"
            onSubmit={handleSubmit(handleCreateEmployees)}
          >
            <EmployeesForm
              nrpList={nrpList}
              control={control}
              errors={errors}
              employeesFields={employeesFields}
              handleAddNewEmployee={handleAddNewEmployee}
              handleRemoveEmployee={handleRemoveEmployee}
              getValues={getValues}
              setValue={setValue}
              t={t}
            />
          </form>
        </DialogContent>

        <DialogActions>
          <Grid
            container
            direction={isDesktop ? "row" : "column-reverse"}
            className="col-flex"
            columnGap={4}
            sx={{ padding: "1.5%" }}
          >
            <Grid item xs={12} sm={2} lg={1} className="full-width">
              <Button
                className="btn__filled__gradient full-width"
                onClick={handleClose}
              >
                {t("App.buttonLabels.cancel")}
              </Button>
            </Grid>
            <Grid item xs={12} sm={4} lg={2.2} className="full-width">
              <Button
                className="btn__applyFilter"
                type="submit"
                form="form-employees"
                startIcon={!loadingCreate ? <SaveIcon /> : <></>}
              >
                {!loadingCreate ? (
                  t("App.buttonLabels.saveChanges")
                ) : (
                  <CircularProgress size={21} color="inherit" />
                )}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      <ModalInfo
        title={modalTitle}
        responseData={createEmployeesData}
        onClose={() => {
          handleClose();
          setOpenModalInfo(false);
        }}
        open={openModalInfo}
      />
    </>
  );
};

export default AddEmployees;
